import api from './api';

// Get these from environment variables
const getTrelloConfig = () => {
  return {
    apiKey: process.env.REACT_APP_TRELLO_API_KEY || '',
    apiToken: process.env.REACT_APP_TRELLO_API_TOKEN || '',
    workspaceId: process.env.REACT_APP_TRELLO_WORKSPACE_ID || '',
  };
};

export interface TrelloBoard {
  id: string;
  name: string;
  desc: string;
  url: string;
  closed: boolean;
  idOrganization: string;
  prefs: {
    background: string;
    backgroundColor: string;
  };
}

export interface TrelloList {
  id: string;
  name: string;
  closed: boolean;
  idBoard: string;
  pos: number;
}

export interface TrelloCard {
  id: string;
  name: string;
  desc: string;
  due: string | null;
  dueComplete: boolean;
  idList: string;
  idBoard: string;
  url: string;
  labels: Array<{
    id: string;
    idBoard: string;
    name: string;
    color: string;
  }>;
  idMembers: string[];
  closed: boolean;
}

export interface TrelloMember {
  id: string;
  fullName: string;
  username: string;
  avatarUrl: string;
}

class TrelloService {
  private getAuthParams() {
    const config = getTrelloConfig();
    return {
      key: config.apiKey,
      token: config.apiToken,
    };
  }

  // Get all boards for the authenticated user
  async getBoards(): Promise<TrelloBoard[]> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/workspace/${config.workspaceId}/boards`, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching Trello boards:', error);
      throw new Error('Failed to fetch Trello boards. Please check your API credentials.');
    }
  }

  // Get a specific board by ID
  async getBoard(boardId: string): Promise<TrelloBoard> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/boards/${boardId}`, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching Trello board:', error);
      throw new Error('Failed to fetch board details.');
    }
  }

  // Get all lists for a board
  async getLists(boardId: string): Promise<TrelloList[]> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/boards/${boardId}/lists`, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching Trello lists:', error);
      throw new Error('Failed to fetch board lists.');
    }
  }

  // Get all cards for a board
  async getCards(boardId: string): Promise<TrelloCard[]> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/boards/${boardId}`, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
        },
      });
      return response.data.cards || [];
    } catch (error) {
      console.error('Error fetching Trello cards:', error);
      throw new Error('Failed to fetch board cards.');
    }
  }

  // Get cards for a specific list
  async getCardsForList(listId: string): Promise<TrelloCard[]> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/lists/${listId}/cards`, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching list cards:', error);
      throw new Error('Failed to fetch cards for this list.');
    }
  }

  // Get card actions/history
  async getCardActions(cardId: string): Promise<any[]> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/cards/${cardId}/actions`, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching card actions:', error);
      throw new Error('Failed to fetch card history.');
    }
  }

  // Export board to Excel (server-side async)
  async exportBoardToExcel(boardId: string): Promise<any> {
    try {
      const config = getTrelloConfig();
      const response = await api.post(`/trello/boards/${boardId}/export`, {}, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting board:', error);
      throw new Error('Failed to export board to Excel.');
    }
  }

  // Get export job status
  async getExportStatus(jobId: string): Promise<any> {
    try {
      const response = await api.get(`/trello/export/${jobId}/status`);
      return response.data;
    } catch (error) {
      console.error('Error checking export status:', error);
      throw new Error('Failed to check export status.');
    }
  }

  // Get dashboard statistics
  async getDashboardStats(boardId: string): Promise<any> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/boards/${boardId}/dashboard-stats`, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw new Error('Failed to fetch dashboard statistics.');
    }
  }

  // Get dashboard job status
  async getDashboardJobStatus(jobId: string): Promise<any> {
    try {
      const response = await api.get(`/trello/dashboard/${jobId}/status`);
      return response.data;
    } catch (error) {
      console.error('Error checking dashboard job status:', error);
      throw new Error('Failed to check dashboard job status.');
    }
  }

  // Get printability analytics snapshot at a specified datetime
  async getPrintabilityAnalytics(boardId: string, snapshotTime: string): Promise<any> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/boards/${boardId}/printability-analytics`, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
          snapshotTime,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching printability analytics:', error);
      throw new Error('Failed to fetch printability analytics.');
    }
  }

  // Get per-day printability bucket counts over a date range
  async getPrintabilityTrend(boardId: string, startDate: string, endDate: string, timeOfDay: string = '23:59'): Promise<any> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/boards/${boardId}/printability-trend`, {
        params: {
          apiKey: config.apiKey,
          token: config.apiToken,
          startDate,
          endDate,
          timeOfDay,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching printability trend:', error);
      throw new Error('Failed to fetch printability trend.');
    }
  }

  // Start bulk lead time analysis job
  async startBulkLeadTime(boardId: string): Promise<any> {
    try {
      const config = getTrelloConfig();
      const response = await api.get(`/trello/boards/${boardId}/bulk-leadtime`, {
        params: { apiKey: config.apiKey, token: config.apiToken },
      });
      return response.data;
    } catch (error) {
      console.error('Error starting bulk lead time analysis:', error);
      throw new Error('Failed to start bulk lead time analysis.');
    }
  }

  // Check bulk lead time job status
  async getBulkLeadTimeStatus(jobId: string): Promise<any> {
    try {
      const response = await api.get(`/trello/bulk-leadtime/${jobId}/status`);
      return response.data;
    } catch (error) {
      console.error('Error checking bulk lead time status:', error);
      throw new Error('Failed to check bulk lead time status.');
    }
  }
}

const trelloService = new TrelloService();
export default trelloService;
